import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MdIconComponent } from '@inditex/mduikit';
import { Store } from '@ngrx/store';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { Category } from '@core/domain/models/category.models';
import { CategoriesSelectors } from '@features/category';
import {
    checkAttachmentName, findCategoryById,
} from '@features/category/application/services/category.utils';
import { getMenuCategory } from '@pages/category/public_api';
import { MenuCategory } from '@shared/header/models/category.model';
import { AttachmentsEnums } from '@shared/header/services/attachments.enums';
import { GoToCategory } from '@shared/navigation';

enum NavActionType {
    'NONE' = 'NONE',
    'NEXT' = 'NEXT',
    'PREV' = 'PREV',
}
@Component({
    selector: 'category-nav',
    templateUrl: './category-nav.component.html',
    standalone: true,
    imports: [CommonModule, MdIconComponent],
})
export class CategoryNavComponent implements OnInit, OnDestroy {
    public static STUDIO_TYPE = '22';

    public categoryPrev: MenuCategory;

    public categoryNext: MenuCategory;

    @Input() public isRtl = false;

    private readonly destroySubject = new Subject<void>();

    private currentCategory: MenuCategory;

    private parentCategoriesNavigation: MenuCategory[] = [];

    private categories: MenuCategory[] = [];

    private readonly getMenuCategory$ = this.store.select(getMenuCategory);

    private readonly getMenuCategories$ = this.store.select(CategoriesSelectors.getMenuCategories);

    private readonly getCategoriesNavigation$ = this.store.select(CategoriesSelectors.getCategoriesNavigation);

    constructor(
        private readonly store: Store,
    ) { }

    public ngOnInit(): void {
        combineLatest([this.getMenuCategories$, this.getCategoriesNavigation$])
            .pipe(takeUntil(this.destroySubject))
            .subscribe(([categories, categoriesNavigation]) => {
                this.categories = JSON.parse(JSON.stringify(categories));
                this.parentCategoriesNavigation = categoriesNavigation.filter(
                    (category: Category) => category.type === '22' && !checkAttachmentName(category, AttachmentsEnums.HIDDEN)
                );
            });

        this.getMenuCategory$
            .pipe(
                // eslint-disable-next-line rxjs/no-unsafe-takeuntil
                takeUntil(this.destroySubject),
                distinctUntilChanged((prev, curr) => prev.currentCategory.id === curr.currentCategory.id),
                map((menuCategory) => menuCategory.currentCategory)
            )
            .subscribe((currentCategory) => {
                this.currentCategory = currentCategory;
                this.initNavigatorCategories(NavActionType.NONE);
            });
    }

    public goToPrev(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        this.initNavigatorCategories(NavActionType.PREV);
    }

    public goToNext(event: MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
        this.initNavigatorCategories(NavActionType.NEXT);
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    public initNavigatorCategories(action: NavActionType) {
        if (this.parentCategoriesNavigation) {
            const indexNormal = this.parentCategoriesNavigation.findIndex((cat) => cat.id === this.currentCategory.id);

            for (let i = 1; i <= indexNormal; i++) {
                const nextItem = this.parentCategoriesNavigation[indexNormal + i];
                if (!!nextItem && findCategoryById(this.categories, nextItem.id)) {
                    this.categoryNext = nextItem;
                    break;
                }
            }

            for (let j = 1; j <= indexNormal; j++) {
                const prevItem = this.parentCategoriesNavigation[indexNormal - j];
                if (findCategoryById(this.categories, prevItem.id)) {
                    this.categoryPrev = prevItem;
                    break;
                }
            }

            let categoryId: string;
            switch (action) {
                case NavActionType.NEXT:
                    categoryId = this.categoryNext?.id || null;
                    break;
                case NavActionType.PREV:
                    categoryId = this.categoryPrev?.id || null;
                    break;

                default:
                    break;
            }
            if (categoryId) {
                this.store.dispatch(GoToCategory({ categoryId }));
            }
        }
    }

    public ngOnDestroy() {
        this.destroySubject.next();
        this.destroySubject.complete();
    }
}
